// src/ui/screens/post/widgets/HomeAdItem.jsx

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../../../../firebase';
import { collection, query, where, orderBy, getDocs, doc, updateDoc, increment } from 'firebase/firestore';

const AD_ASPECT_RATIO = 179.4074 / 443.8519;

export const HomeAdItem = () => {
  const navigate = useNavigate();
  const [ads, setAds] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);

  const pageContainerRef = useRef(null);
  const autoTimerRef = useRef(null);
  const touchStartX = useRef(0);
  const touchEndX = useRef(0);

  // ── Fetch ads ──────────────────────────────────────────────────────────────

  useEffect(() => {
    const fetchAds = async () => {
      try {
        const q = query(
          collection(db, 'ads'),
          where('isActive', '==', true),
          orderBy('createdAt', 'desc')
        );
        const snapshot = await getDocs(q);
        const adList = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setAds(adList);
      } catch (err) {
        console.error('Fetch ads error:', err);
        setError('Failed to load ads');
      } finally {
        setIsLoading(false);
      }
    };

    fetchAds();
  }, []);

  // ── Auto-swipe timer ──────────────────────────────────────────────────────

  useEffect(() => {
    if (ads.length <= 1) return;

    autoTimerRef.current = setInterval(() => {
      const next = (currentIndex + 1) % ads.length;
      setCurrentIndex(next);
    }, 5000);

    return () => {
      if (autoTimerRef.current) {
        clearInterval(autoTimerRef.current);
      }
    };
  }, [ads.length, currentIndex]);

  // ── Touch handlers for manual swipe ──────────────────────────────────────

  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchMove = (e) => {
    touchEndX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = () => {
    const diff = touchStartX.current - touchEndX.current;
    if (Math.abs(diff) > 50) {
      if (diff > 0 && currentIndex < ads.length - 1) {
        setCurrentIndex(currentIndex + 1);
      } else if (diff < 0 && currentIndex > 0) {
        setCurrentIndex(currentIndex - 1);
      }
    }
  };

  // ── Navigate to ad details ──────────────────────────────────────────────
  // NOTE: The registered route for ad details is `/listing/:id/details`
  // (see App.js), not `/ad/:id`. Navigating anywhere else means the tap
  // silently goes nowhere useful. We pass the full ad doc via router state
  // so AdDetailsScreen can render immediately without an extra Firestore read,
  // exactly like ListingPage does for property listings.

  const handleAdClick = async (ad) => {
    try {
      await updateDoc(doc(db, 'ads', ad.id), {
        clickCount: increment(1),
      });
    } catch (err) {
      console.error('Error updating ad click count:', err);
    }
    navigate(`/listing/${ad.id}/details`, { state: { adData: ad } });
  };

  // ── Render ad image ──────────────────────────────────────────────────────

  const renderAdImage = (ad) => {
    return (
      <img
        src={ad.imageUrl}
        alt={ad.title || 'Ad'}
        className="w-full h-full object-cover"
        onError={(e) => {
          e.target.style.display = 'none';
        }}
      />
    );
  };

  const renderAdPlaceholder = (ad) => {
    return (
      <div className="w-full h-full flex flex-col items-center justify-center" style={{ backgroundColor: '#23262C' }}>
        <svg className="w-8 h-8 text-white/30" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
        <span className="text-white/40 text-xs mt-1">{ad.title || 'Ad'}</span>
      </div>
    );
  };

  // ── Loading state ─────────────────────────────────────────────────────────

  if (isLoading) {
    const adHeight = window.innerWidth * AD_ASPECT_RATIO;
    return (
      <div
        className="w-full rounded-xl flex items-center justify-center"
        style={{ height: Math.min(adHeight, 200), backgroundColor: '#23262C' }}
      >
        <div className="w-6 h-6 border-2 border-filatom-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // ── Error / Empty state ──────────────────────────────────────────────────

  if (error || ads.length === 0) {
    const adHeight = window.innerWidth * AD_ASPECT_RATIO;
    return (
      <div
        className="w-full rounded-xl flex flex-col items-center justify-center"
        style={{ height: Math.min(adHeight, 200), backgroundColor: '#23262C' }}
      >
        <svg className="w-8 h-8 text-white/30" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
        </svg>
        <span className="text-white/40 text-xs mt-1">
          {error || 'No ads available'}
        </span>
      </div>
    );
  }

  // ── Main render ──────────────────────────────────────────────────────────

  const adHeight = Math.min(window.innerWidth * AD_ASPECT_RATIO, 200);
  const currentAd = ads[currentIndex];

  return (
    <div
      ref={pageContainerRef}
      className="relative w-full rounded-xl overflow-hidden cursor-pointer"
      style={{ height: adHeight }}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      onClick={() => handleAdClick(currentAd)}
    >
      {/* Ad Image */}
      <div className="w-full h-full">
        {currentAd.imageUrl ? renderAdImage(currentAd) : renderAdPlaceholder(currentAd)}
      </div>

      {/* Gradient overlay for text readability */}
      <div className="absolute inset-0 pointer-events-none bg-gradient-to-t from-black/40 via-transparent to-transparent" />

      {/* Ad title overlay */}
      {currentAd.title && (
        <div className="absolute bottom-3 left-3 right-3 pointer-events-none">
          <span className="text-white text-sm font-semibold drop-shadow-lg line-clamp-2">
            {currentAd.title}
          </span>
        </div>
      )}

      {/* Indicator dots */}
      {ads.length > 1 && (
        <div className="absolute bottom-2 left-0 right-0 flex justify-center gap-1.5">
          {ads.map((_, i) => (
            <div
              key={i}
              className={`h-1.5 rounded-full transition-all duration-250 ${
                i === currentIndex ? 'w-4 bg-white' : 'w-1.5 bg-white/40'
              }`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default HomeAdItem;